import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import api from '../../services/leadAutomationService';

const empty = {
  enabled: false, business_segment: '', business_description: '',
  target_audience: '', offers: [], conversion_goal: '',
  qualification_questions: [], required_lead_fields: ['nome', 'telefone'],
  faq: [], handoff_rules: [], conversion_stage_id: '', closing_message: '',
};
const rows = value => String(value || '').split('\n').map(v => v.trim()).filter(Boolean);
const faqText = value => typeof value === 'string' ? value : (value || [])
  .map(item => item.question + ' | ' + item.answer).join('\n');
const parseFaq = value => rows(value).map(line => {
  const separator = line.indexOf('|');
  return separator < 0 ? null : {
    question: line.slice(0, separator).trim(),
    answer: line.slice(separator + 1).trim(),
  };
}).filter(item => item?.question && item?.answer);
const lists = [
  ['offers', 'Produtos ou serviços'],
  ['qualification_questions', 'Perguntas de qualificação'],
  ['handoff_rules', 'Regras para transferir ao atendente'],
];
const texts = [
  ['business_description', 'Descrição da empresa'],
  ['target_audience', 'Público-alvo'],
  ['closing_message', 'Mensagem de encerramento'],
];

export default function CommercialPlaybookCard() {
  const [form, setForm] = useState(empty);
  const [faqDraft, setFaqDraft] = useState('');
  const [meta, setMeta] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const set = (key, value) => setForm(old => ({ ...old, [key]: value }));

  useEffect(() => {
    api.getCommercialPlaybook().then(data => {
      setForm({ ...empty, ...data.playbook });
      setFaqDraft(faqText(data.playbook?.faq));
      setMeta({ mode: data.automation_mode, stages: data.conversion_stages || [] });
    }).catch(() => setNotice(['danger', 'Falha ao carregar o playbook comercial.']))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const saved = await api.updateCommercialPlaybook({
        ...form,
        faq: parseFaq(faqDraft),
        conversion_stage_id: form.conversion_stage_id || null,
      });
      setForm({ ...empty, ...saved });
      setFaqDraft(faqText(saved.faq));
      setNotice(['success', 'Playbook comercial atualizado.']);
    } catch (error) {
      const errors = error.response?.data?.errors;
      setNotice(['danger', (errors && Object.values(errors).flat()[0])
        || error.response?.data?.message || 'Falha ao salvar.']);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" />;
  return <PlaybookForm form={form} faqDraft={faqDraft} setFaqDraft={setFaqDraft}
    meta={meta} notice={notice} saving={saving} set={set} save={save} />;
}

const PlaybookForm = ({ form, faqDraft, setFaqDraft, meta, notice, saving, set, save }) => (
  <div style={{ background: '#fff', border: '1px solid #dbeafe', borderRadius: 14, padding: 20, marginBottom: 16 }}>
    <h5>Playbook Comercial</h5>
    <small className="text-muted">Contexto autorizado para automação de qualquer segmento.</small>
    <Form.Check className="my-3" type="switch" label="Ativar playbook comercial"
      checked={Boolean(form.enabled)}
      onChange={event => set('enabled', event.target.checked)} />
    {meta.mode === 'clinical' && <Alert variant="info">
      A automação clínica permanece principal enquanto Consultório estiver ativo.
    </Alert>}
    {notice && <Alert variant={notice[0]}>{notice[1]}</Alert>}
    <div className="row g-3">
      {[
        ['business_segment', 'Segmento da empresa'],
        ['conversion_goal', 'Objetivo da conversão'],
      ].map(([key, label]) => <Form.Group className="col-md-6" key={key}>
        <Form.Label>{label}</Form.Label>
        <Form.Control value={form[key] || ''}
          onChange={event => set(key, event.target.value)} />
      </Form.Group>)}
      {texts.map(([key, label]) => <TextArea key={key} label={label}
        value={form[key]} onChange={value => set(key, value)} />)}
      {lists.map(([key, label]) => <TextArea key={key}
        label={label + ' (um por linha)'} value={(form[key] || []).join('\n')}
        onChange={value => set(key, rows(value))} />)}
    </div>
    <div className="row g-3 mt-1">
      <TextArea label="FAQ — Pergunta | Resposta" value={faqText(form.faq)}
        onChange={value => { setFaqDraft(value); set('faq', value); }} />
      <Form.Group className="col-md-6">
        <Form.Label>Estágio considerado conversão</Form.Label>
        <Form.Select value={form.conversion_stage_id || ''}
          onChange={event => set('conversion_stage_id', event.target.value)}>
          <option value="">Convertido da pipeline do lead</option>
          {(meta.stages || []).map(stage => <option key={stage.id} value={stage.id}>
            {(stage.pipeline?.nome || 'Pipeline') + ' — ' + stage.nome}
          </option>)}
        </Form.Select>
      </Form.Group>
    </div>
    <Form.Group className="mt-3">
      <Form.Label>Dados obrigatórios do lead</Form.Label>
      <div className="d-flex flex-wrap gap-3">
        {[['nome','Nome'],['telefone','Telefone'],['email','E-mail'],
          ['empresa','Empresa'],['cidade','Cidade'],['estado','Estado']].map(([key,label]) =>
          <Form.Check key={key} label={label}
            checked={(form.required_lead_fields || []).includes(key)}
            onChange={() => set('required_lead_fields',
              (form.required_lead_fields || []).includes(key)
                ? form.required_lead_fields.filter(item => item !== key)
                : [...(form.required_lead_fields || []), key])} />)}
      </div>
    </Form.Group>
    <Button className="mt-3" onClick={save} disabled={saving}>
      {saving ? 'Salvando...' : 'Salvar playbook comercial'}
    </Button>
  </div>
);

const TextArea = ({ label, value, onChange }) => <Form.Group className="col-md-6">
  <Form.Label>{label}</Form.Label>
  <Form.Control as="textarea" rows={4} value={value || ''}
    onChange={event => onChange(event.target.value)} />
</Form.Group>;
